import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import './App.css';

function LanguageSwitcher() {
  const { language, toggleLanguage } = useLanguage();
  return (
    <button onClick={toggleLanguage} className="language-switcher">
      {language === 'en' ? (
        <>
          <img src="/social-icons/fr-flag.svg" alt="Français" className="flag-icon" />
          <span>FR</span>
        </>
      ) : (
        <>
          <img src="/social-icons/gb-flag.svg" alt="English" className="flag-icon" />
          <span>EN</span>
        </>
      )}
    </button>
  );
}

const legalContent = {
  en: {
    terms: {
      title: 'Terms of Service',
      updated: 'Last updated: April 22, 2026',
      intro:
        'These Terms govern your use of hickssama.com and the Hickssama creator website, mailing list, and related creator tools.',
      sections: [
        {
          title: 'Use of the site',
          paragraphs: [
            'You may browse the site, visit linked social profiles, and subscribe to updates for lawful personal use.',
            'You may not misuse the site, interfere with its operation, attempt unauthorized access, or impersonate Hickssama or any platform partner.',
          ],
        },
        {
          title: 'Products and external services',
          paragraphs: [
            'Some links on the site lead to external services such as Payhip, BeatStars, TikTok, YouTube, and Instagram. Those services operate under their own terms, policies, and checkout rules.',
            'Hickssama is not responsible for third-party platform outages, payment handling, or policy changes outside this site.',
          ],
        },
        {
          title: 'Mailing list and creator tools',
          paragraphs: [
            'If you submit your email through the site, you consent to receiving updates related to Hickssama content, releases, and creator offers.',
            'Internal creator tools connected to official social platform APIs are used only to review and operate accounts owned or authorized by Hickssama.',
          ],
        },
        {
          title: 'Intellectual property',
          paragraphs: [
            'Site branding, original text, original graphics, product packaging, and Hickssama-owned materials remain the property of Hickssama unless stated otherwise.',
            'References to anime, music, or third-party platforms do not transfer ownership of those properties.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'For support or legal questions, contact support@hickssama.com.',
          ],
        },
      ],
    },
    privacy: {
      title: 'Privacy Policy',
      updated: 'Last updated: April 22, 2026',
      intro:
        'This Privacy Policy explains what data hickssama.com collects, how it is used, and how Hickssama handles creator-related account data.',
      sections: [
        {
          title: 'Information collected',
          paragraphs: [
            'If you join the mailing list, the site collects your email address.',
            'Hosting providers and basic infrastructure services may also process standard technical data such as IP address, browser information, and request logs for security and delivery.',
          ],
        },
        {
          title: 'How information is used',
          paragraphs: [
            'Email addresses are used to send creator updates, release announcements, and relevant Hickssama offers.',
            'Technical data is used to operate, secure, and improve the site.',
          ],
        },
        {
          title: 'Platform account data',
          paragraphs: [
            'For internal creator operations, Hickssama may connect official platform APIs to review account performance, recent video metadata, and operational analytics for accounts owned or authorized by Hickssama.',
            'That connected platform data is used only for internal analysis, content strategy, and account operations. It is not sold.',
          ],
        },
        {
          title: 'Sharing',
          paragraphs: [
            'Personal data is not sold. Data may be processed by service providers required to host the site, deliver emails, or run infrastructure.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'If you have privacy questions or want your mailing-list email removed, contact support@hickssama.com.',
          ],
        },
      ],
    },
  },
  fr: {
    terms: {
      title: "Conditions d'utilisation",
      updated: 'Derniere mise a jour : 22 avril 2026',
      intro:
        "Ces conditions regissent l'utilisation de hickssama.com, du site Hickssama, de la mailing list et des outils relies a l'activite du createur.",
      sections: [
        {
          title: 'Utilisation du site',
          paragraphs: [
            'Vous pouvez consulter le site, visiter les profils sociaux lies et vous inscrire aux mises a jour pour un usage personnel et legal.',
            "Vous ne pouvez pas perturber le fonctionnement du site, tenter d'obtenir un acces non autorise, ni vous faire passer pour Hickssama ou pour un partenaire de plateforme.",
          ],
        },
        {
          title: 'Produits et services externes',
          paragraphs: [
            'Certains liens du site redirigent vers des services externes comme Payhip, BeatStars, TikTok, YouTube et Instagram. Ces services appliquent leurs propres conditions et politiques.',
            'Hickssama n est pas responsable des pannes, traitements de paiement ou changements de politique de ces services tiers.',
          ],
        },
        {
          title: 'Mailing list et outils createur',
          paragraphs: [
            "Si vous envoyez votre email via le site, vous acceptez de recevoir des informations sur les contenus, sorties et offres Hickssama.",
            "Les outils internes relies aux API officielles des plateformes sociales sont utilises uniquement pour gerer et analyser les comptes detenus ou autorises par Hickssama.",
          ],
        },
        {
          title: 'Propriete intellectuelle',
          paragraphs: [
            'La marque du site, les textes originaux, les visuels originaux, le packaging produit et les contenus appartenant a Hickssama restent la propriete de Hickssama sauf mention contraire.',
            "Les references aux animes, musiques ou plateformes tierces n'emportent aucun transfert de propriete sur ces contenus.",
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'Pour toute question support ou legale, contactez support@hickssama.com.',
          ],
        },
      ],
    },
    privacy: {
      title: 'Politique de confidentialite',
      updated: 'Derniere mise a jour : 22 avril 2026',
      intro:
        'Cette politique explique quelles donnees sont collectées par hickssama.com, comment elles sont utilisees et comment les donnees de comptes createur sont traitees.',
      sections: [
        {
          title: 'Informations collectees',
          paragraphs: [
            'Si vous rejoignez la mailing list, le site collecte votre adresse email.',
            "Les prestataires d'hebergement et d'infrastructure peuvent aussi traiter des donnees techniques standards comme l'adresse IP, le navigateur et les journaux de requete pour la securite et la diffusion du site.",
          ],
        },
        {
          title: 'Utilisation des informations',
          paragraphs: [
            "Les adresses email sont utilisees pour envoyer des mises a jour, annonces de sorties et offres Hickssama pertinentes.",
            'Les donnees techniques servent a faire fonctionner, securiser et ameliorer le site.',
          ],
        },
        {
          title: 'Donnees de comptes plateforme',
          paragraphs: [
            "Pour les operations internes du createur, Hickssama peut connecter des API officielles afin de consulter les performances de compte, les metadonnees recentes de videos et certaines donnees d'analyse pour des comptes detenus ou autorises par Hickssama.",
            'Ces donnees sont utilisees uniquement pour lanalyse interne, la strategie de contenu et la gestion operationnelle. Elles ne sont pas revendues.',
          ],
        },
        {
          title: 'Partage',
          paragraphs: [
            "Les donnees personnelles ne sont pas vendues. Elles peuvent etre traitees par des prestataires necessaires a l'hebergement du site, a l'envoi des emails ou au fonctionnement de l'infrastructure.",
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            "Pour toute question relative a la confidentialite ou pour retirer votre email de la mailing list, contactez support@hickssama.com.",
          ],
        },
      ],
    },
  },
};

function SiteFooter({ fixed = true }) {
  const { t } = useLanguage();

  return (
    <footer className={`footer-copyright${fixed ? '' : ' non-fixed'}`}>
      <span>{t('copyright')}</span>
      <span className="footer-separator">|</span>
      <Link to="/terms" className="footer-link">{t('termsOfService')}</Link>
      <span className="footer-separator">|</span>
      <Link to="/privacy" className="footer-link">{t('privacyPolicy')}</Link>
    </footer>
  );
}

function HomePage() {
  const { t } = useLanguage();
  return (
    <div className="main-bg">
      <div className="center-container landing-layout alt-cards">
        <div className="intro-section one-column">
          <img src="/logo512.png" alt={t('introProfileAlt')} className="profile-photo" />
          <div className="intro-text">
            <h1 className="main-title">HICKSSAMA</h1>
            <p className="intro-greeting">{t('introGreeting')}</p>
          </div>
        </div>
        <div className="alt-card-row">
          <div className="alt-card-image">
            <a href="https://payhip.com/Hickssama" target="_blank" rel="noopener noreferrer" className="image-link">
              <div className="image-card">
                <img src="/payhip.png" alt={t('videoQualityPresets')} className="feature-image" />
                <h2>{t('videoQualityPresets')}</h2>
              </div>
            </a>
          </div>
          <div className="alt-card-desc">
            <p className="card-desc">{t('introPresetsDesc')}</p>
          </div>
        </div>
        <div className="alt-card-row alt-card-row-reverse">
          <div className="alt-card-image">
            <a href="https://www.beatstars.com/hickssama" target="_blank" rel="noopener noreferrer" className="image-link">
              <div className="image-card">
                <img src="/beatstars.png" alt={t('prods')} className="feature-image" />
                <h2>{t('prods')}</h2>
              </div>
            </a>
          </div>
          <div className="alt-card-desc">
            <p className="card-desc">{t('introProdsDesc')}</p>
          </div>
        </div>
        <div className="social-follow-section">
          <div className="social-links">
            <a href="https://www.tiktok.com/@hickssama" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/social-icons/tiktok.svg" alt="TikTok" className="social-icon" />
            </a>
            <a href="https://www.youtube.com/@hickssama" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/social-icons/youtube.svg" alt="YouTube" className="social-icon" />
            </a>
            <a href="https://www.instagram.com/hickssama" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/social-icons/instagram.svg" alt="Instagram" className="social-icon" />
            </a>
          </div>
          <Link to="/stay-informed" className="stay-informed-link">{t('stayInformed')}</Link>
          <p className="follow-me-text">{t('followMe')}</p>
        </div>
      </div>
      <SiteFooter fixed={false} />
      <LanguageSwitcher />
    </div>
  );
}

function StayInformed() {
  const { t } = useLanguage();
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    try {
      const res = await fetch(`${API_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus('Subscribed!');
        setEmail('');
      } else {
        const data = await res.json();
        setStatus(data.error || 'Error');
      }
    } catch {
      setStatus('Network error');
    }
  };

  return (
    <div className="main-bg">
      <div className="center-container">
        <Link to="/" className="back-link">{t('backToHome')}</Link>
        <h1 className="main-title">{t('stayInformed')}</h1>
        <form onSubmit={handleSubmit} className="subscribe-form">
          <label className="email-label">{t('email')}</label>
          <input
            type="email"
            required
            placeholder="example@xyz.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="email-input"
            style={{ fontFamily: 'inherit' }}
          />
          <button type="submit" className="send-btn" style={{ fontFamily: 'inherit' }}>{t('send')}</button>
          <div className="status-msg">{status}</div>
        </form>
      </div>
      <SiteFooter />
      <LanguageSwitcher />
    </div>
  );
}

function LegalPage({ documentKey }) {
  const { language, t } = useLanguage();
  const content = legalContent[language][documentKey];

  return (
    <div className="main-bg">
      <div className="center-container legal-container">
        <Link to="/" className="back-link">{t('backToHome')}</Link>
        <div className="legal-card">
          <h1 className="main-title legal-title">{content.title}</h1>
          <p className="legal-updated">{content.updated}</p>
          <p className="legal-intro">{content.intro}</p>
          {content.sections.map((section) => (
            <section key={section.title} className="legal-section">
              <h2>{section.title}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
      </div>
      <SiteFooter fixed={false} />
      <LanguageSwitcher />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/stay-informed" element={<StayInformed />} />
          <Route path="/terms" element={<LegalPage documentKey="terms" />} />
          <Route path="/privacy" element={<LegalPage documentKey="privacy" />} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
}

export default App;
